import { router, useForm, usePage } from '@inertiajs/react'
import {
  Container,
  Chip,
  Typography,
  Paper,
  Tabs,
  Tab,
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  MenuItem,
  DialogActions,
  Menu,
  IconButton,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material'
import { MuiTelInput } from 'mui-tel-input'
import { DataGrid, GridToolbarFilterButton } from '@mui/x-data-grid';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import Layout from './Layout';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  facility_id?: number;
  facility_name?: string;
  facility_location?: string;
  status?: 1 | 2;
}
interface Facility {
  id: number;
  name: string;
  phone: string;
  client_id: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  facility_type: string;
}

interface Props {
  users: User[];    
  facilities: Facility[];
  users_pagination: {
    current_page: number;
    total_pages: number;
    total_count: number;
  };
  facilities_pagination: {
    current_page: number;
    total_pages: number;
    total_count: number;
  };
}

export default function Admin({ users, facilities, users_pagination, facilities_pagination }: Props) {
  const [tab, setTab] = useState('users');

  return (
    <Layout title="Admin Portal">
      <Container sx={{ mt: 10, mb: 4, minWidth: '1000px'}}>
        <Typography variant="h6" mb={2}>Admin Portal</Typography>
        <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Users" value="users" />
          <Tab label="Facilities" value="facilities" />
        </Tabs>
        {tab === 'users' && <UserTable users={users} pagination={users_pagination} />}
        {tab === 'facilities' && <FacilityTable facilities={facilities} pagination={facilities_pagination} />}
      </Container>
    </Layout>
  )
}

function UserModal({ title, open, onClose, user }: { title: string, open: boolean, onClose: () => void, user: User }) {
  const { facilities } = usePage().props;
  const { data, setData, post, errors } = useForm({
    name: user.name,
    email: user.email,
    role: user.role,
    facility_id: user.facility_id
  });

  useEffect(() => {
    setData({
      name: user.name,
      email: user.email, 
      role: user.role,
      facility_id: user.facility_id,
    });
  }, [user]);

  router.on('invalid', (event) => {
    console.log(`An invalid Inertia response was received.`)
    console.log(event.detail.response)
    onClose();
  })

  const saveUser = () => {
    if (user.id > 0) {
      post(`/admin/update_user/${user.id}`, {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          onClose();
        },
      });
    } else {
      post('/admin/add_user', {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          onClose();
        }
      });
    }
  }

  return (
    <Dialog open={open} onClose={onClose} slotProps={{ paper: { elevation: 24, sx: { padding: 3, width: "750px" } } }}>
      <DialogTitle variant="h6" sx={{ p: "3 2" }}>{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: '3 2 3 0' }}>
        <Box sx={{ display: 'flex', pt:2, gap: 3 }}>
          <FormControl sx={{ flex: 1 }}>
            <TextField sx={{ flex: 1 }} label="Name" value={data.name} onChange={(e) => setData('name', e.target.value)} />
            {errors.name && <FormHelperText error>{errors.name}</FormHelperText>}
          </FormControl>
          <FormControl sx={{ flex: 1 }}>
            <TextField sx={{ flex: 1 }} label="Email" value={data.email} onChange={(e) => setData('email', e.target.value)} />
            {errors["account.email"] && <FormHelperText error>{errors["account.email"]}</FormHelperText>}
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <FormControl sx={{ flex: 1 }}>
            <InputLabel id="facility-label">Facility</InputLabel>
            <Select label="Facility" value={data.facility_id} onChange={(e) => setData('facility_id', Number(e.target.value))}>
              {facilities.map((facility: Facility) => (
                <MenuItem value={facility.id}>{facility.name}</MenuItem>
              ))}
            </Select>
            {errors.facility_id && <FormHelperText error>{errors.facility_id}</FormHelperText>}
          </FormControl>
          <FormControl sx={{ flex: 1 }}>
            <InputLabel id="role-label">Role</InputLabel>
            <Select label="Role" value={data.role} onChange={(e) => setData('role', e.target.value)}>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="practitioner">Practitioner</MenuItem>
            </Select>
            {errors.role && <FormHelperText error>{errors.role}</FormHelperText>}
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: '3 2', justifyContent: 'space-between' }}>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button disabled={!data.name || !data.email || !data.role || !data.facility_id} onClick={saveUser} variant="contained" color="primary">Save User</Button>
      </DialogActions>
    </Dialog>
  )
}

function FacilityModal({ title, open, onClose, facility }: { title: string, open: boolean, onClose: () => void, facility: Facility }) {
  const { data, setData, post, wasSuccessful, errors } = useForm({
    name: facility.name,
    phone: facility.phone,
    address: facility.address,
    client_id: facility.client_id,
    city: facility.city,
    state: facility.state,
    zip_code: facility.zip_code,
    facility_type: facility.facility_type,
  });

  useEffect(() => {
    setData({
      name: facility.name,
      phone: facility.phone,
      address: facility.address,
      client_id: facility.client_id,
      city: facility.city,
      state: facility.state,
      zip_code: facility.zip_code,
      facility_type: facility.facility_type,
    });
  }, [facility]);

  const states = ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia',
    'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland',
    'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
    'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
    'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'];
  const facilityTypes = ['clinic', 'hospital', 'general', 'specialty'];

  const saveFacility = () => {
    if (facility.id > 0) {
      post(`/admin/update_facility/${facility.id}`, {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          onClose();
        }
      });
    } else {
      post('/admin/add_facility', {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          onClose();
        }
      });
    }
  }

  return (
    <Dialog open={open} onClose={onClose} slotProps={{ paper: { elevation: 24, sx: { padding: 3, width: '750px' } } }}>
      <DialogTitle variant="h6" sx={{ p: "3 2" }}>{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 3, p: '3 2 3 0' }}>
        <Box sx={{ display: 'flex', pt: 2 }}>
          <FormControl sx={{ flex: 1 }}>
            <TextField sx={{ flex: 1 }} label="Name" value={data.name} onChange={(e) => setData('name', e.target.value)} />
            {errors.name && <FormHelperText error>{ "Name " + errors.name}</FormHelperText>}
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <FormControl sx={{ flex: 1 }}>
            <TextField sx={{ flex: 1 }} label="ID" value={data.client_id} onChange={(e) => setData('client_id', e.target.value)} />
            {errors.client_id && <FormHelperText error>{ "ID " + errors.client_id}</FormHelperText>}
          </FormControl>
          <FormControl sx={{ flex: 1 }}>
            <MuiTelInput sx={{ flex: 1 }} defaultCountry="US" disableDropdown forceCallingCode label="Phone" value={data.phone} onChange={(_value, info) => setData('phone', info.nationalNumber?.slice(0, 10) || '')} />
            {errors.phone && <FormHelperText error>{ "Phone " + errors.phone}</FormHelperText>}
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <FormControl sx={{ flex: 1 }}>
            <TextField sx={{ flex: 1 }} label="Address" value={data.address} onChange={(e) => setData('address', e.target.value)} />
            {errors.address && <FormHelperText error>{ "Address " + errors.address}</FormHelperText>}
          </FormControl>
          <FormControl sx={{ flex: 1 }}>
            <TextField sx={{ flex: 1 }} label="City" value={data.city} onChange={(e) => setData('city', e.target.value)} />
            {errors.city && <FormHelperText error>{ "City " + errors.city}</FormHelperText>}
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <FormControl sx={{ flex: 1 }}>
            <InputLabel id="state-label">State</InputLabel>
            <Select labelId="state-label" label="State" value={data.state} onChange={(e) => setData('state', e.target.value)}>
              {states.map((state) => (
                <MenuItem value={state}>{state}</MenuItem>
              ))}
            </Select>
            {errors.state && <FormHelperText error>{ "State " + errors.state}</FormHelperText>}
          </FormControl>
          <TextField sx={{ flex: 1 }} label="Zip Code" value={data.zip_code} onChange={(e) => setData('zip_code', e.target.value)} />
        </Box>
        <FormControl sx={{ width: '48.5%' }}>
          <InputLabel id="facility-type-label">Facility Type</InputLabel>
          <Select labelId="facility-type-label" label="Facility Type" value={data.facility_type} onChange={(e) => setData('facility_type', e.target.value)}>
            {facilityTypes.map((type) => (
              <MenuItem value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</MenuItem>
            ))}
          </Select>
          {errors.facility_type && <FormHelperText error>{ "Facility Type " + errors.facility_type}</FormHelperText>}
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ p: '3 2', justifyContent: 'space-between' }}>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button 
          onClick={saveFacility} 
          disabled={!data.name || !data.phone || !data.address || !data.city || !data.state || !data.zip_code || !data.facility_type} 
          variant="contained"
          color="primary">Save Facility
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function UserTable({ users, pagination }: { users: User[], pagination: { current_page: number, total_pages: number, total_count: number } }) {
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(false)
  const newUser: User = { id: 0, name: '', email: '', role: '', facility_id: 0 }
  const [menuUser, setMenuUser] = useState<User>(newUser);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const openMenu = (e: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(e.currentTarget);
    setActionMenuOpen(true);
  }

  const openUserModal = (user: User) => {
    setMenuUser(user)
    setUserModalOpen(true)
  }

  const resendInvitation = () => {
    setActionMenuOpen(false);
    router.post(`/admin/resend_invitation/${menuUser.id}`, {
      preserveScroll: true,
      preserveState: true,
    })
  }
  const resetPassword = () => {
    setActionMenuOpen(false);
    router.post(`/admin/reset_password/${menuUser.id}`, {
      preserveScroll: true,
      preserveState: true,
    })
  }
  const closeUserAccount = () => {
    setConfirmationDialogOpen(false);

    router.post(`/admin/delete_user/${menuUser.id}`, {
      preserveScroll: true,
      preserveState: true
    })
  }
  
  return (
    <>
      <UserModal title={`${menuUser.id > 0 ? 'Edit' : 'Add New'} User`} user={menuUser} open={userModalOpen} onClose={() => setUserModalOpen(false)} />
      <DeleteConfirmationDialog title="Close User Account" content={`Are you sure you want to close ${menuUser.name}'s account?`} open={confirmationDialogOpen} onClose={() => setConfirmationDialogOpen(false)} onConfirm={closeUserAccount} />
      <Paper sx={{ width: '100%' }}>
        <DataGrid
          disableRowSelectionOnClick
          rows={users}
          sx={{ border: 0 }}
          slotProps={{
            filterPanel: {
              sx: {
                "& .MuiDataGrid-filterForm": {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: 2,
                  paddingLeft: 1
                }
              },
              filterFormProps: {
                valueInputProps: {
                  variant: 'outlined'
                },
                operatorInputProps: {
                  variant: 'outlined'
                },
                columnInputProps: {
                  variant: 'outlined'
                }
              }
            }
          }}
          slots={{
            toolbar: () => (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 3, pb: 2 }}>
                <GridToolbarFilterButton />
                <Button startIcon={<AddIcon />} onClick={() => { openUserModal(newUser) }} variant="outlined" color="primary">Add New User</Button>
              </Box>
            )
          }}
          columns={[
            { field: 'name', headerName: 'Name', flex: 1, 
              renderCell: (params) => {
                return (
                  <Box>
                    {params.value}
                    { params.row.status === 1 && <Chip 
                      size="small" 
                      label={'INVITE SENT'} 
                      sx={{ borderRadius: '4px', backgroundColor: '#EBB0B0', fontSize: '10px', fontWeight: 500, marginLeft: 0.5, height: '20px'}} 
                    />}
                  </Box>
                )
              }
            },
            { field: 'email', headerName: 'Email', flex: 1 },
            { field: 'facility_name', headerName: 'Facility Name', flex: 1 },
            { field: 'facility_location', headerName: 'Facility Location', flex: 1 },
            { 
              field: 'role', 
              headerName: 'User Type', 
              flex: 1,
              renderCell: (params) => {
                const chipColors: Record<string, "secondary" | "info" | "default"> = {
                  'practitioner': 'default',
                  'manager': 'info',
                  'admin': 'secondary'
                }
                
                const chipColor = chipColors[params.value] || 'default';
                
                return (
                  <Chip 
                    label={params.value}
                    color={chipColor}
                    variant="outlined"
                    sx={{ textTransform: 'capitalize' }}
                  />
                );
              }
            },
            {
              field: 'actions',
              headerName: 'Actions',
              flex: 1,
              sortable: false,
              renderCell: (params) => (
                <IconButton
                  size="large"
                  aria-label="nav bar menu"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={(e) => {
                    e.stopPropagation();
                    openMenu(e);
                    setMenuUser(params.row)
                  }}
                  color="inherit"
                >
                  <MoreHorizIcon />
                </IconButton>
              )
            }
          ]}
          rowCount={users.length}
          disableColumnMenu
        />
      </Paper>
      <Menu anchorEl={userMenuAnchorEl}  open={actionMenuOpen} onClose={() => { setActionMenuOpen(false) }}>
        <MenuItem onClick={() => { setActionMenuOpen(false); setUserModalOpen(true) }}>Edit user</MenuItem>
        <MenuItem onClick={resetPassword}>Reset password</MenuItem>
        <MenuItem onClick={resendInvitation}>Resend invitation</MenuItem>
        <MenuItem onClick={() => { setActionMenuOpen(false); setConfirmationDialogOpen(true) }}>Delete user</MenuItem>
      </Menu>
    </>
  )
}

function FacilityTable({ facilities, pagination }: { facilities: Facility[], pagination: { current_page: number, total_pages: number, total_count: number } }) {
  const [facilityModalOpen, setFacilityModalOpen] = useState(false);
  const [facilityMenuAnchorEl, setFacilityMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const newFacility: Facility = { id: 0, name: '', phone: '', address: '', client_id: '', city: '', state: '', zip_code: '', facility_type: '' };
  const [menuFacility, setMenuFacility] = useState<Facility>(newFacility);
  const openMenu = (e: React.MouseEvent<HTMLElement>) => {
    setFacilityMenuAnchorEl(e.currentTarget);
    setActionMenuOpen(true);
  }

  const openFacilityModal = (facility: Facility) => {
    setMenuFacility(facility)
    setFacilityModalOpen(true)
  }

  const deleteFacility = () => {
    setConfirmationDialogOpen(false);

    router.post(`/admin/delete_facility/${menuFacility.id}`, {
      preserveScroll: true,
      preserveState: true
    })
  }

  return (
    <>
      <FacilityModal title="Add New Facility" facility={menuFacility} open={facilityModalOpen} onClose={() => setFacilityModalOpen(false)} />
      <DeleteConfirmationDialog title="Delete Facility" content="Are you sure you want to delete this facility?" open={confirmationDialogOpen} onClose={() => setConfirmationDialogOpen(false)} onConfirm={deleteFacility} />
      <Paper sx={{ width: '100%' }}>
        <DataGrid
          disableRowSelectionOnClick
          rows={facilities}
          sx={{ border: 0 }}
          slotProps={{
            filterPanel: {
              sx: {
                "& .MuiDataGrid-filterForm": {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: 2,
                  paddingLeft: 1
                }
              },
              filterFormProps: {
                valueInputProps: {
                  variant: 'outlined'
                },
                operatorInputProps: {
                  variant: 'outlined'
                },
                columnInputProps: {
                  variant: 'outlined'
                }
              }
            }
          }}
          slots={{
            toolbar: () => (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 3, pb: 2 }}>
                <GridToolbarFilterButton />
                <Button startIcon={<AddIcon />} onClick={() => openFacilityModal(newFacility)} variant="outlined" color="primary">Add New Facility</Button>
              </Box>
            )
          }}
          columns={[
            { field: 'name', headerName: 'Name', flex: 1, },
            { field: 'phone', headerName: 'Phone', flex: 1, valueFormatter: (value: string) => {
              return value.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
            } },
            { field: 'address', headerName: 'Address', flex: 1 },
            { field: 'city', headerName: 'City', flex: 1 },
            { field: 'state', headerName: 'State', flex: 1 },
            { field: 'zip_code', headerName: 'Zip Code', flex: 1 },
            { field: 'facility_type', headerName: 'Facility Type', flex: 1, valueFormatter: (value: string) => {
              return value[0].toUpperCase() + value.slice(1)
            } },
            { 
              field: 'actions',
              headerName: 'Actions',
              flex: 1,
              sortable: false,
              renderCell: (params) => (
                <IconButton
                  size="large"
                  aria-label="nav bar menu"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={(e) => {
                    e.stopPropagation();
                    openMenu(e);
                    setMenuFacility(params.row)
                  }}
                  color="inherit"
                >
                  <MoreHorizIcon />
                </IconButton>
              )
            }
          ]}
          rowCount={facilities.length}
          disableColumnMenu
        />
      </Paper>
      <Menu anchorEl={facilityMenuAnchorEl} open={actionMenuOpen} onClose={() => { setActionMenuOpen(false) }}>
        <MenuItem onClick={() => { setActionMenuOpen(false); setFacilityModalOpen(true) }}>Edit facility</MenuItem>
        <MenuItem onClick={() => { setActionMenuOpen(false); setConfirmationDialogOpen(true) }}>Delete facility</MenuItem>
      </Menu>
    </>
  )
}